<template>
    <ul class="legend" v-if="props.legendPosition === 'up'">
        <li v-for="(slice, index) in formattedData" :key="index">
            <span class="legend-color" :style="{ backgroundColor: slice.color }"></span>
            {{ slice.label }} <span class="legend-value">{{ slice.percentage }}%</span>
        </li>
    </ul>
    <div class="chart-wrapper">
        <div class="chart-container" ref="chartContainer">
            <canvas ref="canvas"></canvas>
            <div class="center-text" v-html="centerText"></div>
        </div>
    </div>
    <ul class="legend" v-if="props.legendPosition === 'down'">
        <li v-for="(slice, index) in formattedData" :key="index">
            <span class="legend-color" :style="{ backgroundColor: slice.color }"></span>
            {{ slice.label }} <span class="legend-value">{{ slice.percentage }}%</span>
        </li>
    </ul>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, defineProps } from "vue";

const props = defineProps({
    data: Object,
    centerText: {
        type: String,
        default: "Par taille de jante"
    },
    legendPosition: {
        type: String,
        default: 'up'
    },
    reduceColors: {
        type: Boolean,
        default: false
    }
});

const canvas = ref(null);
const chartContainer = ref(null);
const size = ref(200);
let resizeObserver = null;

const generateColors = (count) => {
    const colors = ["#000000", "#FF0000", "#1972C3", "#FF7F3B", "#CBB4A3", "#58595B", "#2D9567",
        "#FF5733", "#3666EA", "#C9A17A", "#4E555A", "#2E8B57", "#FF6384", "#36A2EB",
        "#FFCE56", "#4BC0C0", "9966FF",
        "#FF9F40", "#FFCD56", "#4DC9F6", "#C9CBFF", "#7AC943",
        "#F778BA", "#8D6E63", "#BDB76B", "#DDA0DD",
        "#FFD700", "#DC143C", "#20B2AA", "#9370DB", "#FF4500"];

    if (props.reduceColors) {
        colors.splice(0, 2);
    }
    return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

const formattedData = computed(() => {
    const keys = Object.keys(props.data);
    const values = Object.values(props.data);
    const colors = generateColors(keys.length);
    const total = values.reduce((sum, val) => sum + val, 0);

    const valueForMap = {
        'BS': 'Bridgestone',
        'FS': 'Firestone'
    };

    return keys.map((key, index) => {
        const mappedValue = valueForMap[key];
        const value = Number(values[index]) || 0;
        return {
            label: mappedValue !== undefined ? mappedValue : key,
            value,
            percentage: total > 0 ? ((value / total) * 100).toFixed(1) : "0.0",
            color: colors[index]
        };
    });
});

const drawChart = () => {
    if (!canvas.value || !chartContainer.value) return;
    const ctx = canvas.value.getContext("2d");
    const total = formattedData.value.reduce((sum, slice) => sum + slice.value, 0);
    let startAngle = -0.5 * Math.PI;

    const width = size.value;
    const height = size.value;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = width / 2.2;
    const holeRadius = radius * 0.6;

    canvas.value.width = width;
    canvas.value.height = height;
    ctx.clearRect(0, 0, width, height);

    if (total <= 0) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fillStyle = "#E8E8E8";
        ctx.fill();
    } else {
        formattedData.value.forEach((slice) => {
            const sliceAngle = (slice.value / total) * 2 * Math.PI;
            const endAngle = startAngle + sliceAngle;

            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, startAngle, endAngle);
            ctx.closePath();
            ctx.fillStyle = slice.color;
            ctx.fill();

            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, startAngle, startAngle + 0.05);
            ctx.closePath();
            ctx.fillStyle = "white";
            ctx.fill();

            startAngle = endAngle;
        });
    }

    ctx.beginPath();
    ctx.arc(centerX, centerY, holeRadius, 0, 2 * Math.PI);
    ctx.fillStyle = "white";
    ctx.fill();
};

const updateSize = () => {
    if (chartContainer.value) {
        size.value = Math.min(chartContainer.value.clientWidth, 200);
        drawChart();
    }
};

onMounted(() => {
    updateSize();
    resizeObserver = new ResizeObserver(updateSize);
    if (chartContainer.value) {
        resizeObserver.observe(chartContainer.value);
    }
});

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
});

watch(() => props.data, drawChart, { deep: true });
</script>

<style scoped lang="scss">
@import "@/styles/pie_chart.scss";
</style>
